import { useState, useEffect, useMemo } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { Event, Property, RevenueManagerBranch } from '@/types/revenue-manager';

interface CreateEventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (event: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>) => void;
  properties: Property[];
  branches: RevenueManagerBranch[];
  editingEvent?: Event | null;
  orgId?: string;
  api_key?: string | number;
  isLoading?: boolean;
}

const categories = [
  { value: 'festival', label: 'Festival', icon: '🎉' },
  { value: 'conference', label: 'Conference', icon: '🎤' },
  { value: 'sports_event', label: 'Sports Event', icon: '⚽' },
  { value: 'concert', label: 'Concert', icon: '🎵' },
  { value: 'holiday', label: 'Holiday', icon: '🎄' },
  { value: 'convention', label: 'Convention', icon: '🏢' },
  { value: 'exhibition', label: 'Exhibition', icon: '🎨' },
  { value: 'seasonal', label: 'Seasonal Event', icon: '🌸' },
  { value: 'other', label: 'Other', icon: '📅' },
];

const impacts = [
  { value: 'low', label: 'Low Impact', color: 'bg-blue-500' },
  { value: 'medium', label: 'Medium Impact', color: 'bg-amber-500' },
  { value: 'high', label: 'High Impact', color: 'bg-red-500' },
];

export function CreateEventDialog({
  open,
  onOpenChange,
  onSave,
  properties,
  branches,
  editingEvent,
  orgId,
  api_key,
  isLoading = false,
}: CreateEventDialogProps) {
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  const [startDateTime, setStartDateTime] = useState('');
  const [endDateTime, setEndDateTime] = useState('');
  const [category, setCategory] = useState<Event['category']>('conference');
  const [impact, setImpact] = useState<Event['impact']>('medium');
  const [pricingMultiplier, setPricingMultiplier] = useState(1.2);
  const [description, setDescription] = useState('');
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);

  // Get current datetime in YYYY-MM-DDTHH:mm format for min attribute
  const minDateTime = useMemo(() => {
    return new Date().toISOString().slice(0, 16);
  }, []);

  useEffect(() => {
    if (editingEvent) {
      setName(editingEvent.name);
      setLocation(editingEvent.location || '');
      // Convert ISO to datetime-local format (YYYY-MM-DDTHH:mm)
      setStartDateTime(new Date(editingEvent.startDate).toISOString().slice(0, 16));
      setEndDateTime(new Date(editingEvent.endDate).toISOString().slice(0, 16));
      setCategory(editingEvent.category);
      setImpact(editingEvent.impact);
      setPricingMultiplier(editingEvent.pricingMultiplier);
      setDescription(editingEvent.description || '');

      // Convert branch IDs to cm_property_ids for selection
      const propertyIdsFromBranches = editingEvent.propertyIds
        .map(branchId => {
          const branch = branches.find(b => String(b.id) === branchId);
          if (branch && String(branch.cm_property_id) && String(branch.cm_property_id) !== "") {
            return String(branch.cm_property_id);
          }
          return branchId;
        })
        .filter((id): id is string => id !== undefined);
      setSelectedProperties(propertyIdsFromBranches);
    } else {
      setName('');
      setLocation('');
      setStartDateTime('');
      setEndDateTime('');
      setCategory('conference');
      setImpact('medium');
      setPricingMultiplier(1.2);
      setDescription('');
      setSelectedProperties([]);
    }
  }, [editingEvent, open, branches]);

  // Validation errors
  const validationErrors = useMemo(() => {
    const errors: Record<string, string> = {};

    // Event name validation
    if (!name.trim()) {
      errors.name = 'Event name is required';
    } else if (name.trim().length < 3) {
      errors.name = 'Event name must be at least 3 characters';
    } else if (name.trim().length > 100) {
      errors.name = 'Event name must be less than 100 characters';
    }

    // DateTime validation
    if (!startDateTime) {
      errors.startDateTime = 'Start date and time is required';
    }

    if (!endDateTime) {
      errors.endDateTime = 'End date and time is required';
    } else if (new Date(endDateTime) < new Date(startDateTime)) {
      errors.endDateTime = 'End date/time must be after start date/time';
    }

    // Pricing multiplier validation
    if (pricingMultiplier < 0.5) {
      errors.pricingMultiplier = 'Multiplier must be at least 0.5';
    } else if (pricingMultiplier > 3) {
      errors.pricingMultiplier = 'Multiplier cannot exceed 3.0';
    }

    // Properties validation
    if (selectedProperties.length === 0) {
      errors.properties = 'Select at least one property';
    }

    return errors;
  }, [name, startDateTime, endDateTime, pricingMultiplier, selectedProperties]);

  const isFormValid = Object.keys(validationErrors).length === 0;

  const toggleProperty = (propertyId: string) => {
    setSelectedProperties((prev) =>
      prev.includes(propertyId)
        ? prev.filter((id) => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const priceChangePercent = ((pricingMultiplier - 1) * 100).toFixed(0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid) {
      return;
    }

    onSave({
      org_id: orgId || '105',
      name,
      location: location || undefined,
      startDate: startDateTime.split('T')[0], // YYYY-MM-DD format
      endDate: endDateTime.split('T')[0], // YYYY-MM-DD format
      category,
      impact,
      pricingMultiplier,
      description: description || undefined,
      propertyIds: selectedProperties,
    });
    // Don't close here - let parent handle closing based on API response
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {editingEvent ? 'Edit Event' : 'Create New Event'}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Add a local event that will impact pricing for selected properties
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="eventName">Event Name <span className="text-destructive">*</span></Label>
              <Input
                id="eventName"
                placeholder="e.g., IPL Final"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={validationErrors.name ? 'border-destructive' : ''}
              />
              {validationErrors.name && (
                <p className="text-xs text-destructive">{validationErrors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="eventLocation">Location <span className="text-muted-foreground">(Optional)</span></Label>
              <Input
                id="eventLocation"
                placeholder="e.g., Mumbai Stadium"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDateTime">Start Date & Time <span className="text-destructive">*</span></Label>
                <Input
                  id="startDateTime"
                  type="datetime-local"
                  value={startDateTime}
                  onFocus={() => {
                    if (!startDateTime) {
                      const today = new Date().toISOString().slice(0, 10);
                      setStartDateTime(`${today}T00:00`);
                    }
                  }}
                  onChange={(e) => setStartDateTime(e.target.value)}
                  min={editingEvent ? undefined : minDateTime}
                  className={validationErrors.startDateTime ? 'border-destructive' : ''}
                />
                {validationErrors.startDateTime && (
                  <p className="text-xs text-destructive">{validationErrors.startDateTime}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="endDateTime">End Date & Time <span className="text-destructive">*</span></Label>
                <Input
                  id="endDateTime"
                  type="datetime-local"
                  value={endDateTime}
                  onFocus={() => {
                    if (!endDateTime) {
                      const today = new Date().toISOString().slice(0, 10);
                      setEndDateTime(`${today}T00:00`);
                    }
                  }}
                  onChange={(e) => setEndDateTime(e.target.value)}
                  min={startDateTime || (editingEvent ? undefined : minDateTime)}
                  className={validationErrors.endDateTime ? 'border-destructive' : ''}
                />
                {validationErrors.endDateTime && (
                  <p className="text-xs text-destructive">{validationErrors.endDateTime}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category <span className="text-destructive">*</span></Label>
                <Select value={category} onValueChange={(v) => setCategory(v as Event['category'])}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        <div className="flex items-center gap-2">
                          <span>{cat.icon}</span>
                          <span>{cat.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Demand Impact <span className="text-destructive">*</span></Label>
                <Select value={impact} onValueChange={(v) => setImpact(v as Event['impact'])}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {impacts.map((imp) => (
                      <SelectItem key={imp.value} value={imp.value}>
                        <div className="flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full ${imp.color}`} />
                          <span>{imp.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Pricing Multiplier <span className="text-destructive">*</span></Label>
              <Input
                type="number"
                step="0.05"
                min="0.5"
                max="3"
                value={pricingMultiplier}
                onChange={(e) => setPricingMultiplier(Number(e.target.value))}
                className={validationErrors.pricingMultiplier ? 'border-destructive' : ''}
              />
              <p className="text-sm text-accent">
                Prices will {pricingMultiplier >= 1 ? 'increase' : 'decrease'} by{' '}
                {Math.abs(Number(priceChangePercent))}%
              </p>
              {validationErrors.pricingMultiplier && (
                <p className="text-xs text-destructive">{validationErrors.pricingMultiplier}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Description <span className="text-muted-foreground">(Optional)</span></Label>
              <Textarea
                placeholder="Add details about this event..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Affected Properties <span className="text-destructive">*</span></Label>
              <div className={cn(
                "rounded-lg border border-border p-4 space-y-2",
                validationErrors.properties && "border-destructive"
              )}>
                {properties.map((property) => (
                  <div
                    key={property.id}
                    className={`flex items-center gap-3 p-2 rounded-lg transition-colors ${selectedProperties.includes(property.id)
                      ? 'bg-accent/10 border border-accent'
                      : 'hover:bg-muted'
                      }`}
                  >
                    <Checkbox
                      checked={selectedProperties.includes(property.id)}
                      onCheckedChange={() => toggleProperty(property.id)}
                    />
                    <div>
                      <p className="font-medium">{property.name}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {property.location}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              {validationErrors.properties && (
                <p className="text-xs text-destructive">{validationErrors.properties}</p>
              )}
            </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isFormValid || isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                editingEvent ? 'Update Event' : 'Create Event'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}